import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ReceiptScannerFlow from "@/components/receipt-scanner/ReceiptScannerFlow";
import DuplicateReceiptDialog from "@/components/DuplicateReceiptDialog";
import { Loader2 } from "lucide-react";
import { normalizeMerchantName } from "@/lib/merchantNormalizer";
import { updateFinancialSummary, isBusinessExpense, checkDuplicateReceipt } from "@/lib/receiptUtils";
import { getReceiptErrorMessage } from "@/lib/receiptErrorMessages";

export default function NewReceiptScanner() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateData, setDuplicateData] = useState<{
    extracted: any;
    uploadedPaths: string[];
  } | null>(null);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [uploadedImagePaths, setUploadedImagePaths] = useState<string[]>([]);
  const [capturedImageUrls, setCapturedImageUrls] = useState<string[]>([]);

  const saveReceiptToDatabase = async (extracted: any, uploadedPaths: string[], isDuplicate: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const receiptDate = new Date(extracted.purchase_date);

      // Normalize merchant name for consistent matching
      const normalizedMerchantName = normalizeMerchantName(extracted.store_name);

      // Check merchant history for auto-categorization
      const { data: merchantData } = await supabase
        .from('merchants')
        .select('common_categories, total_receipts')
        .eq('normalized_name', normalizedMerchantName)
        .eq('user_id', user.id)
        .single();

      // Create receipt record with all intelligent fields
      const { data: receipt, error: insertError } = await supabase
        .from('receipts')
        .insert({
          user_id: user.id,
          file_path: uploadedPaths[0],
          merchant_name: normalizedMerchantName,
          date: extracted.purchase_date,
          subtotal: extracted.subtotal || 0,
          tax: extracted.tax || 0,
          total: extracted.total || 0,
          amount: extracted.total || 0,
          category: extracted.item_categories?.[0] || 'Other',
          line_items: extracted.items,
          item_categories: extracted.item_categories,
          deductible_items: extracted.deductible_items,
          non_deductible_items: extracted.non_deductible_items,
          tax_savings_estimate: extracted.tax_savings_estimate || 0,
          deduction_explanation: extracted.deduction_explanation,
          confidence_score: extracted.confidence_score || 0,
          is_duplicate: isDuplicate,
          status: extracted.confidence_score >= 60 ? 'approved' : 'needs_review',
          parsed_json: {
            ...extracted,
            photo_urls: uploadedPaths
          }
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Update or create merchant record
      const { error: merchantError } = await supabase
        .from('merchants')
        .upsert({
          user_id: user.id,
          merchant_name: normalizedMerchantName,
          normalized_name: normalizedMerchantName,
          last_seen: new Date().toISOString(),
          common_categories: merchantData?.common_categories || extracted.item_categories || [],
          total_receipts: (merchantData?.total_receipts ?? 0) + 1 // FIX: Increment counter correctly
        }, {
          onConflict: 'user_id,normalized_name',
          ignoreDuplicates: false
        });

      if (merchantError && process.env.NODE_ENV !== "production") {
        console.error("Merchant update error:", merchantError);
      }

      // Update financial summary using helper function
      const isBusiness = isBusinessExpense(extracted);
      await updateFinancialSummary(
        extracted.purchase_date,
        extracted.total || 0,
        isBusiness,
        extracted.tax_savings_estimate || 0,
        'add'
      );

      // Show appropriate message based on confidence score
      if (extracted.confidence_score < 60) {
        toast({
          title: "Receipt Saved - Please Review",
          description: "Low confidence score detected. Please verify the details.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success!",
          description: `Receipt processed successfully. Tax savings: $${extracted.tax_savings_estimate?.toFixed(2) || '0.00'}`,
        });
      }

      navigate(`/receipt/${receipt.id}`);
    } catch (error: any) {
      console.error("Error saving receipt:", error);
      
      // Get user-friendly error message
      const userMessage = getReceiptErrorMessage(error.message || error);
      
      toast({
        title: "Save Failed",
        description: userMessage,
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleComplete = async (imageUrls: string[], confirmedData: any) => {
    // If we have confirmed data, save it directly
    if (confirmedData) {
      setIsUploading(true);
      try {
        // Transform the data structure to match our database schema
        const extracted = {
          store_name: confirmedData.storeName,
          store_address: confirmedData.storeAddress,
          purchase_date: confirmedData.date,
          time: confirmedData.time,
          currency: confirmedData.currency || 'CAD',
          subtotal: confirmedData.subtotal,
          tax: confirmedData.tax,
          total: confirmedData.total,
          payment_method: confirmedData.paymentMethod,
          items: confirmedData.items,
          confidence_score: confirmedData.overallConfidence * 100,
          item_categories: [],
          deductible_items: [],
          non_deductible_items: [],
          tax_savings_estimate: 0
        };

        // Check for duplicates
        const normalizedMerchantName = normalizeMerchantName(extracted.store_name || '');
        const { data: existingReceipts } = await supabase
          .from('receipts')
          .select('id, merchant_name, total, date')
          .eq('merchant_name', normalizedMerchantName)
          .eq('date', extracted.purchase_date)
          .eq('total', extracted.total);

        const isDuplicate = existingReceipts && existingReceipts.length > 0;

        if (isDuplicate) {
          setDuplicateData({ extracted, uploadedPaths: uploadedImagePaths });
          setDuplicateDialogOpen(true);
          setIsUploading(false);
          return;
        }

        await saveReceiptToDatabase(extracted, uploadedImagePaths, false);
      } catch (error: any) {
        console.error("Error saving receipt:", error);
        const userMessage = error.message || getReceiptErrorMessage(error);
        toast({
          title: "Save Failed",
          description: userMessage,
          variant: "destructive",
        });
        navigate("/receipts");
      } finally {
        setIsUploading(false);
      }
      return;
    }

    // Otherwise, process the images
    setIsUploading(true);
    setCapturedImageUrls(imageUrls);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Convert blob URLs to Files and upload to storage
      const uploadedPaths: string[] = [];
      
      for (let i = 0; i < imageUrls.length; i++) {
        const response = await fetch(imageUrls[i]);
        const blob = await response.blob();
        const file = new File([blob], `receipt-${Date.now()}-${i}.jpg`, { type: 'image/jpeg' });

        const fileName = `${user.id}/${crypto.randomUUID()}.jpg`;
        const { error: uploadError } = await supabase.storage
          .from('receipts')
          .upload(fileName, file);

        if (uploadError) throw uploadError;
        uploadedPaths.push(fileName);
      }

      setUploadedImagePaths(uploadedPaths);

      toast({
        title: "Analyzing Receipt",
        description: "Processing your receipt images with AI...",
      });

      // Call AI parsing function
      console.log("Invoking parse-multi-receipt with file paths:", uploadedPaths);
      const { data: parseData, error: parseError } = await supabase.functions.invoke(
        "parse-multi-receipt",
        {
          body: { filePaths: uploadedPaths },
        }
      );

      console.log("Parse response:", parseData, parseError);

      if (parseError) {
        console.error("Parse function error:", parseError);
        toast({
          title: "Analysis Failed",
          description: "Failed to analyze receipt. Please try again.",
          variant: "destructive",
        });
        setIsUploading(false);
        setExtractedData({});
        setCapturedImageUrls(uploadedPaths);
        return;
      }

      // Validate response structure
      if (!parseData || parseData.success !== true || !parseData.data) {
        console.error("Invalid parse response structure:", parseData);
        toast({
          title: "Analysis Failed",
          description: "Receipt analysis failed. Please review manually.",
          variant: "destructive",
        });
        setIsUploading(false);
        setExtractedData({});
        setCapturedImageUrls(uploadedPaths);
        return;
      }

      // Map the parsed data
      const parsed = parseData.data;
      console.log("Extracted data:", parsed);

      // Check for duplicates before showing review
      const isDuplicate = await checkDuplicateReceipt(
        parsed.storeName || "Unknown",
        parsed.date || new Date().toISOString().split("T")[0],
        parsed.total || 0
      );

      if (isDuplicate) {
        setDuplicateDialogOpen(true);
        setExtractedData(parsed);
        setCapturedImageUrls(uploadedPaths);
        setIsUploading(false);
        return;
      }

      // Show review screen with extracted data
      setExtractedData(parsed);
      setCapturedImageUrls(uploadedPaths);
      setIsUploading(false);

    } catch (error: any) {
      console.error("Error processing receipt:", error);
      toast({
        title: "Processing Error",
        description: error.message || "Failed to process receipt",
        variant: "destructive",
      });
      
      // Still show review screen with empty data for manual entry
      setExtractedData({});
      setCapturedImageUrls(imageUrls);
      setIsUploading(false);
    }
  };

  const handleContinueAnyway = async () => {
    if (!duplicateData) return;

    setDuplicateDialogOpen(false);
    setIsUploading(true);

    try {
      await saveReceiptToDatabase(duplicateData.extracted, duplicateData.uploadedPaths, true);
    } catch (error) {
      navigate("/receipts");
    } finally {
      setIsUploading(false);
      setDuplicateData(null);
    }
  };

  const handleCancelDuplicate = async () => {
    if (!duplicateData) return;

    setDuplicateDialogOpen(false);

    try {
      // Clean up uploaded files
      for (const path of duplicateData.uploadedPaths) {
        await supabase.storage.from('receipts').remove([path]);
      }
      
      toast({
        title: "Cancelled",
        description: "Receipt upload cancelled and files removed.",
      });
    } catch (error) {
      console.error("Error cleaning up files:", error);
    } finally {
      setDuplicateData(null);
      navigate("/receipts");
    }
  };

  if (isUploading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <h2 className="text-xl font-semibold text-foreground">Analyzing Receipt</h2>
        <p className="text-muted-foreground text-center mt-2">
          Processing images with AI...
        </p>
      </div>
    );
  }

  return (
    <>
      <ReceiptScannerFlow
        open={true}
        onClose={() => {
          setExtractedData(null);
          setCapturedImageUrls([]);
          navigate("/receipts");
        }}
        onComplete={handleComplete}
        extractedData={extractedData}
        capturedImages={capturedImageUrls}
      />
      
      {duplicateData && (
        <DuplicateReceiptDialog
          open={duplicateDialogOpen}
          onContinue={handleContinueAnyway}
          onCancel={handleCancelDuplicate}
          merchantName={normalizeMerchantName(duplicateData.extracted.store_name)}
          date={duplicateData.extracted.purchase_date}
          total={duplicateData.extracted.total}
          currency={duplicateData.extracted.currency || "CAD"}
        />
      )}
    </>
  );
}
